/*
 * USP Slider — sections/td-usp-slider.liquid
 *
 * The horizontal scrolling itself is native (component-slider.css). This element
 * only adds the two things CSS cannot do:
 *   1. a scroll-position progress bar driven by CSS custom properties
 *   2. pausing card videos while they are off-screen
 */

if (!customElements.get('td-usp-slider')) {
  class TdUspSlider extends HTMLElement {
    connectedCallback() {
      this.scroller = this.querySelector('[id^="Slider-"]');
      this.progressWrapper = this.querySelector('.td-usp-slider__progress-wrapper');
      this.frame = null;

      if (this.scroller) {
        this.handleScroll = this.onScroll.bind(this);
        this.scroller.addEventListener('scroll', this.handleScroll, { passive: true });

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.updateProgress());
          this.resizeObserver.observe(this.scroller);
        }

        this.updateProgress();
      }

      this.initVideos();
    }

    disconnectedCallback() {
      if (this.scroller && this.handleScroll) {
        this.scroller.removeEventListener('scroll', this.handleScroll);
      }
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.videoObserver) this.videoObserver.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateProgress();
      });
    }

    updateProgress() {
      if (!this.progressWrapper || !this.scroller) return;

      const { scrollWidth, clientWidth, scrollLeft } = this.scroller;

      // Nothing to scroll (all cards fit, or the slider is disabled at this breakpoint).
      if (scrollWidth - clientWidth <= 1) {
        this.progressWrapper.hidden = true;
        return;
      }

      this.progressWrapper.hidden = false;

      const visibleRatio = Math.min(Math.max(clientWidth / scrollWidth, 0.05), 1);
      const offsetRatio = Math.min(Math.abs(scrollLeft) / scrollWidth, 1 - visibleRatio);

      this.style.setProperty('--td-usp-progress-width', `${visibleRatio * 100}%`);
      this.style.setProperty('--td-usp-progress-offset', `${offsetRatio * 100}%`);
    }

    initVideos() {
      const videos = Array.from(this.querySelectorAll('video'));
      if (videos.length === 0) return;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        videos.forEach((video) => {
          video.autoplay = false;
          video.pause();
        });
        return;
      }

      if (!('IntersectionObserver' in window)) return;

      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (entry.isIntersecting) {
              const played = video.play();
              // Safari rejects play() when the tab is backgrounded; that is not an error worth surfacing.
              if (played && typeof played.catch === 'function') played.catch(() => {});
            } else if (!video.paused) {
              video.pause();
            }
          });
        },
        { threshold: 0.25 }
      );

      videos.forEach((video) => {
        // Programmatic playback is only permitted while muted.
        video.muted = true;
        this.videoObserver.observe(video);
      });
    }
  }

  customElements.define('td-usp-slider', TdUspSlider);
}
